import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { Beat } from "../../../../plugins/spinners.plugin";
import Card from "../../../../components/cards/admin/cards";
import Button from "../../../../components/buttons/buttons";
import Table from "../../../../components/tables/tableCols1";
import TableSkeleton from "../../../../components/skeleton/table";
import { writeFileWithXLSX } from "../../../../components/exports/xlsx";
import EmptyState from "../../../../assets/images/emptyState/payment.svg";
import {
  filterRevenuePayouts,
  searchRevenuePayout,
  getAllRevenuePayoutsNoPagination,
} from "../../../../redux/revenuePayout/revenuePayoutActions";
import { showShipper } from "../../../../redux/shippers/shippersActions";
import { RequestPayoutModal } from "../../../../components/modals/admin/requestPayoutModal";

function numberWithCommas(x) {
  return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

const formatDate = (date) =>
  date ? moment(date).format("DD MMM YYYY") : "N/A";

export default function PayOut() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const { downloading } = useSelector((state) => state.revenuePayout);

  // null while loading. Kept locally, not from the store: the other tab and the
  // business pages share allRevenuePayout, so it could show their payouts here
  const [payouts, setPayouts] = useState(null);
  const [shipper, setShipper] = useState(null);
  const isLoading = payouts === null;

  const payLoad = useMemo(
    () => ({
      shipper_id: id,
      type: "shipper",
      paid_out: 0,
    }),
    [id],
  );

  const refreshPayouts = async () => {
    const response = await dispatch(filterRevenuePayouts(payLoad));
    setPayouts(response?.data ?? {});
  };

  useEffect(() => {
    let ignore = false;
    setPayouts(null);
    setShipper(null);
    setSearchResults(null);

    const fetchData = async () => {
      const [payoutsResponse, shipperResponse] = await Promise.all([
        dispatch(filterRevenuePayouts(payLoad)),
        dispatch(showShipper(id)),
      ]);
      if (ignore) return;
      setPayouts(payoutsResponse?.data ?? {});
      setShipper(shipperResponse?.data ?? null);
    };

    if (id) fetchData();
    return () => {
      ignore = true;
    };
  }, [dispatch, id, payLoad]);

  const shipperName = shipper?.shipper_name || shipper?.name;

  function createTableData(data) {
    if (Array.isArray(data)) {
      const outputArray = [];
      data.forEach((payout, index) => {
        const single = {
          num: <span className="text-base font-semibold">{index + 1}</span>,
          requestId: payout?.id || "N/A",
          amount: <span>₦{numberWithCommas(payout?.amount || 0)}</span>,
          requestDate: formatDate(payout?.created_at),
          status: (
            <span className="inline-flex rounded-full px-2 py-1 text-xs font-semibold bg-yellow-100 text-yellow-800">
              {payout?.status}
            </span>
          ),
          payout: payout,
        };

        outputArray.push({ single });
      });

      return outputArray;
    }
    return [];
  }

  function createTableDataDownload(data) {
    if (Array.isArray(data)) {
      return data.map((payout) => ({
        request_id: payout?.request_id,
        shipper: shipperName,
        amount: payout?.amount,
        request_date: formatDate(payout?.request_date || payout?.created_at),
        status: "Pending",
      }));
    }
    return [];
  }

  const data = createTableData(searchResults ?? payouts?.data);

  const handleChange = (e) => {
    e.preventDefault();
    setSearch(e.target.value);
    // Clearing the search box goes back to the full list
    if (e.target.value.length === 0) {
      setSearchResults(null);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      doSearch();
    }
  };

  async function doSearch() {
    if (!search) return;
    const response = await dispatch(
      searchRevenuePayout({ ...payLoad, search }),
    );
    setSearchResults(response?.data?.data ?? response?.data ?? []);
  }

  const handleDownload = async () => {
    const response = await dispatch(
      getAllRevenuePayoutsNoPagination({ ...payLoad, pagination: 0 }),
    );
    if (response) {
      writeFileWithXLSX(createTableDataDownload(response?.data), {
        filename: `${shipperName ?? "Shipper"}_Payout_Requests.xlsx`,
        download: true,
      });
    }
  };

  const goToSinglePage = (params) => {
    const requestId = params?.single?.payout?.id;
    navigate(`/admin/revenue/shipper/${id}/payout/${requestId}`);
  };

  const columns = useMemo(
    () => [
      {
        Header: "",
        hideHeader: false,
        id: "checkbox-table-column",
        columns: [
          {
            Header: "S/N",
            accessor: "single.num",
          },
          {
            Header: "Request ID",
            accessor: "single.requestId",
          },
          {
            Header: "Amount",
            accessor: "single.amount",
          },
          {
            Header: "Request Date",
            accessor: "single.requestDate",
          },
          {
            Header: "Status",
            accessor: "single.status",
          },
        ],
      },
    ],
    [],
  );

  return (
    <div>
      <Card.TableFilter
        title={[
          shipperName,
          payouts?.total > 0 &&
            `Showing ${payouts?.from ?? "0"} - ${payouts?.to ?? "0"} of ${
              payouts?.total ?? "0"
            } Payouts`,
        ]
          .filter(Boolean)
          .join(" · ")}
        searchPlaceholder={"Search Payouts"}
        onChange={handleChange}
        handleKeyDown={handleKeyDown}
      >
        <RequestPayoutModal
          ownerId={id}
          type="shipper"
          onSuccess={refreshPayouts}
        />
        <Button.Secondary
          title={downloading ? <Beat color={"#ffffff"} /> : "Download CSV"}
          disabled={downloading || !data?.length}
          onClick={handleDownload}
        />
      </Card.TableFilter>

      <div className="border-b border-[#E4E4F3] dark:border-[#505050]"></div>

      {isLoading ? (
        <TableSkeleton />
      ) : (
        <div className="bg-white dark:bg-[#121212] rounded-md py-4 px-3">
          {data?.length > 0 ? (
            <Table
              columns={columns}
              data={data}
              onClick={goToSinglePage}
              goToSinglePage={goToSinglePage}
            />
          ) : (
            <div className="text-center py-12">
              <img
                src={EmptyState}
                alt="empty"
                className="w-32 h-32 mx-auto mb-4"
              />
              <p className="text-gray-500 dark:text-gray-400">
                {searchResults
                  ? "No payout requests match your search"
                  : "No payout requests found"}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
